import { useNavigate, Link } from "react-router-dom";
import { Phone, Gem, CheckCircle2, ArrowRight, Clock } from "lucide-react";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { MobileBar } from "@/components/landing/MobileBar";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/landing/Reveal";
import { SEO_PAGES, PHONE, PHONE_TEL } from "@/lib/site";

const WEDDING = SEO_PAGES.find((p) => p.kind === "service");

const FAQ = [
  {
    q: "How soon after the wedding should I clean my dress?",
    a: "As soon as you can. Sugar-based stains like champagne and cake are invisible at first and turn brown over months.",
  },
  {
    q: "How much does wedding dress cleaning and preservation cost?",
    a: "It depends on the fabric, beadwork and staining. Call 480-940-6023 for a free quote after a quick look at your gown.",
  },
  {
    q: "How long does it take?",
    a: "About 1 week from pickup to delivery. Need it sooner for an event? Call us and we'll do our best.",
  },
  {
    q: "Do you pick up wedding dresses?",
    a: "Yes, free, anywhere on our routes in Chandler, Gilbert, Tempe, Ahwatukee, Sun Lakes and Maricopa.",
  },
];

const BEFORE_WEDDING = [
  "Pre-ceremony steaming and pressing",
  "Bridal alterations and fittings by our in-house seamstresses",
  "Bridesmaid dresses, suits and tuxedos",
];

const STEPS = [
  "Call 480-940-6023 or book a free consultation online.",
  "We pick up your gown at your door, free.",
  "We inspect it with you, treat the stains, clean and preserve it.",
  "We deliver it back, boxed and ready to store.",
];

const GALLERY = [
  {
    src: "/images/wedding_gown.png",
    alt: "Freshly cleaned white wedding gown with lace bodice and tulle skirt on a padded hanger at the Oxford Cleaners shop",
    caption: "Hand-finished gowns at our Chandler shop",
  },
  {
    src: null,
    alt: "Wedding gown folded with acid-free tissue inside a museum-style preservation box — photo coming soon",
    caption: "Preservation boxing — photo coming soon",
  },
  {
    src: null,
    alt: "Close-up of beadwork and lace being hand-inspected before cleaning — photo coming soon",
    caption: "Bead-by-bead inspection — photo coming soon",
  },
  {
    src: null,
    alt: "Bride's bouquet resting on a freshly cleaned and pressed gown — photo coming soon",
    caption: "Before the big day — photo coming soon",
  },
];

const SERVICE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Wedding dress cleaning and preservation",
  provider: { "@id": "https://oxford-cleaners.com/#business" },
  areaServed: [
    { "@type": "City", name: "Chandler, AZ" },
    { "@type": "City", name: "Gilbert, AZ" },
    { "@type": "City", name: "Tempe, AZ" },
    { "@type": "Place", name: "Ahwatukee, Phoenix, AZ" },
    { "@type": "Place", name: "Sun Lakes, AZ" },
    { "@type": "City", name: "Maricopa, AZ" },
  ],
};

export default function WeddingDressPage() {
  const navigate = useNavigate();
  const book = () => navigate("/", { state: { scrollTo: "book" } });

  return (
    <div className="bg-white text-slate-900">
      <Seo title={WEDDING.title} description={WEDDING.meta} path={WEDDING.path} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(SERVICE_SCHEMA) }} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        }}
      />
      <Header />
      <main>
        <section className="relative overflow-hidden bg-gradient-to-b from-amber-50 via-white to-white pb-20 pt-44">
          <div className="mx-auto grid max-w-6xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2">
            <Reveal>
              <p className="inline-flex items-center gap-2 rounded-full border border-fall-amber/40 bg-white px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-fall-orange">
                <Gem className="h-3.5 w-3.5" /> Oxford Cleaners · Bridal Care
              </p>
              <h1 className="mt-5 font-display text-3xl font-extrabold leading-tight tracking-tight text-brand-navy sm:text-4xl lg:text-5xl">
                Wedding Dress Cleaning &amp; Preservation in Chandler, AZ
              </h1>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-600">
                Your gown carried the day. We&apos;ll carry it forever. Oxford Cleaners hand-cleans and
                preserves wedding dresses for brides across Chandler, Gilbert, Tempe, Ahwatukee, Sun Lakes
                and Maricopa, with free pickup at your door and a personal consultation before any work begins.
              </p>
              <div className="mt-9 flex flex-wrap gap-3">
                <button
                  data-testid="wedding-page-book-button"
                  onClick={book}
                  className="rounded-full bg-brand-navy px-7 py-3.5 font-display text-sm font-extrabold uppercase tracking-widest text-white shadow-lg transition-transform hover:scale-105"
                >
                  Book my gown pickup
                </button>
                <a
                  data-testid="wedding-page-call-link"
                  href={PHONE_TEL}
                  className="flex items-center gap-2 rounded-full border-2 border-brand-navy px-7 py-3 text-sm font-bold text-brand-navy transition-colors hover:bg-brand-navy hover:text-white"
                >
                  <Phone className="h-4 w-4" /> Call {PHONE} for a quote
                </a>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="overflow-hidden rounded-[2rem] border-4 border-white shadow-2xl shadow-amber-100">
                <img
                  src="/images/wedding_gown.png"
                  alt="Elegant white wedding gown on a satin hanger under a warm spotlight, professionally cleaned by Oxford Cleaners"
                  className="aspect-[4/5] w-full object-cover"
                />
              </div>
            </Reveal>
          </div>
        </section>

        <section className="bg-white py-20">
          <div className="mx-auto grid max-w-6xl gap-6 px-4 sm:px-6 lg:grid-cols-2">
            <Reveal>
              <div className="h-full rounded-3xl border border-amber-100 bg-[#FDF9F3] p-8">
                <h2 className="font-display text-xl font-bold tracking-tight text-brand-navy sm:text-2xl">Wedding dress cleaning</h2>
                <p className="mt-4 text-base leading-relaxed text-slate-600">
                  Every gown is inspected bead by bead before cleaning. We treat the stains that set in on the
                  big day — grass, makeup, champagne, food, and dirty dance-floor hems — using gentle,
                  fabric-safe processes made for lace, silk, tulle and beadwork.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="h-full rounded-3xl border border-amber-100 bg-[#FDF9F3] p-8">
                <h2 className="font-display text-xl font-bold tracking-tight text-brand-navy sm:text-2xl">Wedding dress preservation</h2>
                <p className="mt-4 text-base leading-relaxed text-slate-600">
                  After cleaning, your gown is folded with acid-free tissue and sealed in a museum-style
                  keepsake box. This keeps out light, dust and the yellowing that ruins stored dresses, so
                  it&apos;s ready for a daughter, a vow renewal, or just a look back.
                </p>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="bg-[#FDF9F3] py-20">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <Reveal>
              <h2 className="font-display text-xl font-bold tracking-tight text-brand-navy sm:text-2xl">Before the wedding</h2>
              <ul className="mt-6 grid gap-3 sm:grid-cols-3">
                {BEFORE_WEDDING.map((b) => (
                  <li key={b} className="flex items-start gap-2.5 rounded-2xl border border-amber-100 bg-white p-5 text-sm font-semibold text-slate-700">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-fall-orange" /> {b}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </section>

        <section data-testid="wedding-gallery" className="bg-white py-20">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <Reveal>
              <h2 className="font-display text-xl font-bold tracking-tight text-brand-navy sm:text-2xl">From our bridal floor</h2>
              <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                {GALLERY.map((g, i) => (
                  <figure key={i} data-testid={`wedding-gallery-${i}`} className="overflow-hidden rounded-3xl border border-amber-100 bg-[#FDF9F3]">
                    {g.src ? (
                      <img src={g.src} alt={g.alt} className="h-56 w-full object-cover" />
                    ) : (
                      <div role="img" aria-label={g.alt} className="flex h-56 w-full flex-col items-center justify-center gap-2 border-b border-dashed border-amber-200 text-amber-300">
                        <Gem className="h-8 w-8" />
                        <span className="px-4 text-center text-xs font-semibold text-slate-400">{g.alt.split("—")[1]?.trim() || "Photo coming soon"}</span>
                      </div>
                    )}
                    <figcaption className="px-4 py-3 text-center text-xs font-bold uppercase tracking-widest text-slate-500">
                      {g.caption}
                    </figcaption>
                  </figure>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

        <section className="bg-brand-navy py-20 text-white">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <Reveal>
              <h2 className="font-display text-xl font-bold tracking-tight sm:text-2xl">How it works</h2>
              <ol className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                {STEPS.map((step, i) => (
                  <li key={i} className="rounded-3xl border border-white/10 bg-white/5 p-6 backdrop-blur">
                    <span className="text-outline font-display text-4xl font-extrabold">0{i + 1}</span>
                    <p className="mt-3 text-sm leading-relaxed text-slate-200">{step}</p>
                  </li>
                ))}
              </ol>
            </Reveal>
          </div>
        </section>

        <section className="bg-white py-20">
          <div className="mx-auto max-w-4xl px-4 sm:px-6">
            <Reveal>
              <h2 className="font-display text-xl font-bold tracking-tight text-brand-navy sm:text-2xl">Wedding dress FAQ</h2>
              <div className="mt-6 space-y-4">
                {FAQ.map((f) => (
                  <div key={f.q} className="rounded-2xl border border-amber-100 bg-[#FDF9F3] p-6">
                    <h3 className="font-display text-base font-bold text-brand-navy">{f.q}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.a}</p>
                  </div>
                ))}
              </div>
              <div className="mt-10 flex flex-wrap gap-3">
                <button
                  onClick={book}
                  className="rounded-full bg-brand-navy px-7 py-3.5 font-display text-sm font-extrabold uppercase tracking-widest text-white transition-transform hover:scale-105"
                >
                  Book my gown pickup
                </button>
                <a
                  href={PHONE_TEL}
                  className="flex items-center gap-2 rounded-full border-2 border-brand-navy px-7 py-3 text-sm font-bold text-brand-navy transition-colors hover:bg-brand-navy hover:text-white"
                >
                  <Phone className="h-4 w-4" /> Call {PHONE} for a quote
                </a>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="bg-[#FDF9F3] py-16">
          <div className="mx-auto max-w-4xl px-4 sm:px-6">
            <Reveal>
              <h2 className="font-display text-lg font-bold text-brand-navy sm:text-xl">We pick up across the East Valley</h2>
              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                {SEO_PAGES.filter((p) => p.kind === "city").map((c) => (
                  <Link
                    key={c.path}
                    to={c.path}
                    className="group flex items-center justify-between rounded-2xl border border-amber-100 bg-white px-5 py-4 text-sm font-bold text-brand-navy transition-colors hover:border-fall-amber"
                  >
                    {c.city} dry cleaning
                    <ArrowRight className="h-4 w-4 text-fall-orange transition-transform group-hover:translate-x-1" />
                  </Link>
                ))}
              </div>
              <p className="mt-8 flex items-center gap-2 text-sm font-semibold text-slate-500">
                <Clock className="h-4 w-4 text-fall-orange" />
                5865 W Ray Rd, Suite 3, Chandler, AZ 85226 · Mon–Fri 7am–5pm · Sat 10am–2pm
              </p>
            </Reveal>
          </div>
        </section>
      </main>
      <Footer />
      <MobileBar />
    </div>
  );
}
